import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ListItem, ListItemText, Badge, Box, Tooltip} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {JoinedRoomData} from '@/redux-store/slices/matrix/room-data';
import {
  formatDateByLocale,
  locales
} from '@/components/matrix-chat/utils/helper-functions';
import {DocumentType, SupportedLanguage} from '@/types/common';
import i18n from '@/i18n/i18n';
import {useMatrixUnreadEventsContext} from '@/contexts/matrix/unread-events';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import ChatWindow from './chat-window';
import {MatrixRoomProvider} from '@/contexts/matrix/room/provider';
import {useMatrixContext} from '@/contexts/matrix/client';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import {MatrixMembership} from '@/types/matrix';
import PendingAction from './pending-action';
import EventDetailsDialog from '@/components/events/details';
import GroupDetailsDialog from '@/components/groups/details';
import {getEventById} from '@/rest/events/get-event';
import {getGroupById} from '@/rest/groups/get-group';
import {EventData} from '@/types/event';
import {GroupData} from '@/types/group';
import {useAppSelector} from '@/redux-store/store';
import {translateMessage} from '@/services/matrix/messages';

interface ChatItemProps {
  roomId?: string;
  chat?: JoinedRoomData;
  notOpenChat?: boolean;
  shouldOpenDirectly?: boolean;
  onWindowClose?: () => void;
  onSelectChat?: (chat: JoinedRoomData) => void;
}

interface ChatItemContentProps {
  chat: JoinedRoomData;
  shouldOpenDirectly?: boolean;
  notOpenChat?: boolean;
  onWindowClose?: () => void;
  onSelectChat?: (chat: JoinedRoomData) => void;
}

const ChatItemContent: React.FC<ChatItemContentProps> = ({
  chat,
  shouldOpenDirectly = false,
  onWindowClose,
  notOpenChat,
  onSelectChat
}) => {
  const {t} = useTranslation();
  const currentLocale: SupportedLanguage = i18n.language as SupportedLanguage;
  const locale = locales[currentLocale];
  const [userName, setUserName] = useState<string | null>(null);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<
    EventData | GroupData | null
  >(null);
  const [hasOpenedDirectly, setHasOpenedDirectly] = useState(false);

  const eventViewRef = useRef<{reloadEvents: () => void}>(null);

  const {unreadEventsCount} = useMatrixUnreadEventsContext();
  const {initMatrixRoom, handleCloseWindowChat, windowChatOpen} =
    useMatrixRoomContext();

  const {matrixClient, rooms} = useMatrixContext();

  const room = rooms?.find((r) => r.roomId === chat.roomId);

  const handleStartWindowChat = useCallback(
    (roomId: string) => {
      try {
        if (chat.isDirect && !notOpenChat) {
          initMatrixRoom(undefined, roomId);
        } else {
          if (chat.metadata.documentType === 'event' && !notOpenChat) {
            getEventById(chat.documentId)
              .then((event) => {
                setSelectedDocument(event);
                setDocumentOpen(true);
              })
              .catch((error) => {
                console.error('Error fetching event:', error);
              });
          } else if (chat.metadata.documentType === 'team' && !notOpenChat) {
            getGroupById(chat.documentId)
              .then((group) => {
                setSelectedDocument(group);
                setDocumentOpen(true);
              })
              .catch((error) => {
                console.error('Error fetching group:', error);
              });
          }
        }
      } catch (error) {
        console.error('error: ', error);
      }
    },
    [initMatrixRoom, chat, notOpenChat]
  );

  const handleCloseDocumentDetails = useCallback(() => {
    setDocumentOpen(false);
    setHasOpenedDirectly(false);
    if (onWindowClose) {
      onWindowClose();
    }
  }, [onWindowClose]);

  const handleCloseDirectChat = useCallback(() => {
    handleCloseWindowChat();
    setHasOpenedDirectly(false);
    if (onWindowClose) {
      onWindowClose();
    }
  }, [handleCloseWindowChat, onWindowClose]);

  const handleExternalReload = () => {
    if (eventViewRef.current) {
      eventViewRef.current.reloadEvents();
    }
  };
  const handleGetTopic = useCallback(() => {
    if (chat.isDirect) {
      if (chat.lastMessage?.senderName) {
        return t('direct_chat_with', {
          userName: chat.metadata.topic
        });
      }
      return t('direct_chat');
    } else {
      if (chat.metadata.documentType === DocumentType.EVENT) {
        return `${t('event')}: ${chat.metadata.topic}`;
      } else if (chat.metadata.documentType === DocumentType.TEAM) {
        return `${t('group')}: ${chat.metadata.topic}`;
      }
    }
  }, [chat, t]);
  const handleFormatLastMessage = useCallback(() => {
    if (!chat.lastMessage) {
      return t('no_messages');
    }

    const formattedDate = formatDateByLocale(
      new Date(chat.lastChange),
      locale,
      t,
      true,
      true
    );

    if (chat.lastMessage.system) {
      return `${t(chat.lastMessage.message)} - ${formattedDate}`;
    }

    return `${chat.lastMessage.senderName}: ${translateMessage(
      chat.lastMessage.message,
      t
    )} - ${formattedDate}`;
  }, [chat, locale, t]);

  useEffect(() => {
    if (shouldOpenDirectly && !hasOpenedDirectly) {
      handleStartWindowChat(chat.roomId);
      setHasOpenedDirectly(true);
    }
  }, [
    shouldOpenDirectly,
    chat.roomId,
    handleStartWindowChat,
    hasOpenedDirectly
  ]);

  useEffect(() => {
    const fetchUserName = async () => {
      if (matrixClient && chat.isDirect && chat.lastMessage) {
        const userName = chat.metadata.topic;
        setUserName(userName);
      }
    };

    fetchUserName();
  }, [chat, matrixClient]);

  if (room?.getMyMembership() === MatrixMembership.INVITE) {
    return (
      <PendingAction
        status={MatrixMembership.INVITE}
        topic={chat.metadata.topic}
        roomId={chat.roomId}
      />
    );
  }

  if (room?.getMyMembership() === MatrixMembership.KNOCK) {
    return (
      <PendingAction
        status={MatrixMembership.KNOCK}
        topic={chat.metadata.topic}
        roomId={chat.roomId}
      />
    );
  }

  return (
    <>
      {!shouldOpenDirectly && (
        <ListItem
          onClick={() => {
            if (chat) {
              onSelectChat?.(chat);
            }

            handleStartWindowChat(chat.roomId);
          }}
          sx={{
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.hover,
              cursor: 'pointer'
            }
          }}
        >
          <ListItemText
            primary={
              <Box display="flex" alignItems="center">
                <ResponsiveTypography
                  sx={{
                    fontWeight: 'bold'
                  }}
                >
                  {handleGetTopic()}
                </ResponsiveTypography>
                <Tooltip title={t('unread_events_count')}>
                  <Badge
                    badgeContent={unreadEventsCount}
                    color="primary"
                    sx={{
                      marginLeft: 2.5,
                      marginBottom: 1,
                      transform: 'scale(1.2)'
                    }}
                  />
                </Tooltip>
              </Box>
            }
            secondary={
              <Box>
                <ResponsiveTypography>
                  {handleFormatLastMessage()}
                </ResponsiveTypography>
              </Box>
            }
          />
        </ListItem>
      )}

      {chat.isDirect ? (
        <ChatWindow
          open={windowChatOpen}
          onClose={handleCloseDirectChat}
          userName={userName}
        />
      ) : chat.metadata.documentType === DocumentType.EVENT &&
        selectedDocument ? (
        <EventDetailsDialog
          open={documentOpen}
          onClose={handleCloseDocumentDetails}
          event={selectedDocument as EventData}
          onChange={handleExternalReload}
          defaultChatTab
        />
      ) : chat.metadata.documentType === DocumentType.TEAM &&
        selectedDocument ? (
        <GroupDetailsDialog
          open={documentOpen}
          onClose={handleCloseDocumentDetails}
          group={selectedDocument as GroupData}
          defaultChatTab
        />
      ) : null}
    </>
  );
};

const ChatItem: React.FC<ChatItemProps> = ({
  roomId,
  chat,
  notOpenChat,
  shouldOpenDirectly = false,
  onWindowClose,
  onSelectChat
}) => {
  const chatDataFromStore = useAppSelector((state) =>
    roomId
      ? state.matrixRooms.roomsJoined.find((room) => room.roomId === roomId)
      : null
  );

  const chatData = chat || chatDataFromStore;

  if (!chatData) {
    return null;
  }

  return (
    <MatrixRoomProvider roomId={chatData.roomId}>
      <ChatItemContent
        chat={chatData}
        shouldOpenDirectly={shouldOpenDirectly}
        onWindowClose={onWindowClose}
        notOpenChat={notOpenChat}
        onSelectChat={onSelectChat}
      />
    </MatrixRoomProvider>
  );
};

export default ChatItem;
